import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
   Grid,
  Card, 
  CardMedia, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert
} from '@mui/material';

import { 
  DirectionsBoat as BoatIcon, 
  Search as SearchIcon, 
  People as PeopleIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { getActiveVessels } from '../firebase/services/vesselService';
import { Vessel } from '../models/types';

const VesselListingPage: React.FC = () => {
  const location = useLocation();
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [filteredVessels, setFilteredVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [boatTypeFilter, setBoatTypeFilter] = useState('all');
  
  useEffect(() => {
    const fetchVessels = async () => {
      try {
        setLoading(true);
        const fetchedVessels = await getActiveVessels();
        setVessels(fetchedVessels);
        setFilteredVessels(fetchedVessels);
      } catch (err: any) {
        console.error('Error fetching vessels:', err);
        setError('Failed to load boats. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVessels();
  }, []);
  
  // Check if we have a vessel type filter from the modal
  useEffect(() => {
    const state = location.state as { vesselType?: string } | null;
    if (state?.vesselType) {
      setBoatTypeFilter(state.vesselType.toLowerCase());
    }
  }, [location.state]);
  
  useEffect(() => {
    // Apply filters
    let results = vessels;
    
    // Search term filter
    if (searchTerm) {
      results = results.filter(vessel => 
        vessel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vessel.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vessel.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Boat type filter
    if (boatTypeFilter !== 'all') {
      results = results.filter(vessel => 
        vessel.type.toLowerCase() === boatTypeFilter.toLowerCase()
      );
    }
    
    setFilteredVessels(results);
  }, [searchTerm, boatTypeFilter, vessels]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleBoatTypeChange = (e: SelectChangeEvent) => {
    setBoatTypeFilter(e.target.value);
  };
  
  // Get unique boat types for filter
  const boatTypes = ['all', ...new Set(vessels.map(vessel => vessel.type.toLowerCase()))];
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <BoatIcon sx={{ mr: 1 }} /> Our Fleet
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Browse our selection of high-quality rental boats for your lake adventure.
        </Typography>
        
        {/* Filters */}
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search boats..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="boat-type-filter-label">Boat Type</InputLabel>
            <Select
              labelId="boat-type-filter-label"
              id="boat-type-filter"
              value={boatTypeFilter}
              label="Boat Type"
              onChange={handleBoatTypeChange}
            >
              {boatTypes.map(type => (
                <MenuItem 
                  key={type} 
                  value={type}
                >
                  {type === 'all' ? 'All Types' : type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}
        
        {/* Loading indicator */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredVessels.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            No boats found matching your criteria. Try adjusting your filters.
          </Alert>
        ) : (
          <Grid container spacing={4}>
            {filteredVessels.map((vessel) => (
              <Grid
                item
                key={vessel.id}
                xs={12}
                sm={6}
                md={4}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={vessel.images && vessel.images.length > 0 ? vessel.images[0] : 'https://source.unsplash.com/random/?boat'}
                    alt={vessel.name}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2">
                      {vessel.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {vessel.description.substring(0, 100)}...
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Up to {vessel.capacity} people
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      component={Link} 
                      to={`/vessels/${vessel.id}`}
                    >
                      View Details
                    </Button>
                    <Button 
                      size="small" 
                      color="primary" 
                      component={Link} 
                      to={`/reservation/${vessel.id}`}
                    >
                      Make Reservation
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default VesselListingPage; 